import { useEffect, useState } from "react";
import axios from "axios";
import Logo from "@/components/landing/Logo";
import { Loader2, LogOut, Inbox, KeyRound, Download, Trash2, PhoneCall, Building2, Inbox as LeadsIcon, Image as ImageIcon, Flame, FileText, MessageSquare } from "lucide-react";
import { useLocation } from "react-router-dom";
import ConversationsPanel from "@/components/admin/ConversationsPanel";
import CustomersPanel from "@/components/admin/CustomersPanel";
import MediaPanel from "@/components/admin/MediaPanel";
import WarmLeadsPanel from "@/components/admin/WarmLeadsPanel";
import EstimatesPanel from "@/components/admin/EstimatesPanel";
import MessagesPanel from "@/components/admin/MessagesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRADE_FILTERS = [
  { value: "all", label: "All trades" },
  { value: "hvac", label: "HVAC" },
  { value: "plumbers", label: "Plumbers" },
  { value: "electricians", label: "Electricians" },
  { value: "roofers", label: "Roofers" },
  { value: "contractors", label: "General Contractors" },
];

const tradeLabel = (v) => TRADE_FILTERS.find((t) => t.value === v)?.label || v || "—";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full rounded-md border border-white/[0.1] bg-[#0B0D10] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20";

export default function Admin() {
  const location = useLocation();
  const googleError = location.state?.authError;
  const [token, setToken] = useState(() => localStorage.getItem("bf_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "leads");
  const [msgUnread, setMsgUnread] = useState(0);
  const [unread, setUnread] = useState(0);

  const logout = () => {
    localStorage.removeItem("bf_admin_token");
    setToken(null);
    setLeads(null);
  };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLeads(r.data))
      .catch((e) => {
        if (e.response?.status === 401) logout();
        else setLeads([]);
      });
  }, [token]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("bf_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.next !== pwForm.confirm) {
      setPwMsg({ ok: false, text: "New passwords don't match." });
      return;
    }
    setPwLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPwMsg({ ok: true, text: "Password updated. Use it next time you sign in." });
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwMsg({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setPwLoading(false);
    }
  };

  const deleteLead = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setDeleting(null);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0D10] px-5">
        <form
          onSubmit={submitLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-xl border border-white/[0.1] bg-[#12161F] p-8"
        >
          <div className="flex items-center gap-2.5">
            <Logo size={32} />
            <span className="font-display text-lg font-bold text-slate-50">Backfill Admin</span>
          </div>
          <div className="mt-7 space-y-4">
            <input
              type="email"
              required
              data-testid="admin-email-input"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
            <input
              type="password"
              required
              data-testid="admin-password-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">
              {error}
            </p>
          )}
          {googleError && !error && (
            <p data-testid="google-auth-error" className="mt-4 text-sm text-red-400">
              {googleError}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={submitting}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-[#F25F22] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
          <div className="mt-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/[0.08]" />
            <span className="text-[11px] uppercase tracking-wider text-slate-600">or</span>
            <span className="h-px flex-1 bg-white/[0.08]" />
          </div>
          <button
            type="button"
            data-testid="google-login-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/admin";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-white/[0.12] bg-white/[0.03] py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-[#F25F22]/50"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
            </svg>
            Sign in with Google
          </button>
          <a href="/" data-testid="admin-back-home" className="mt-4 block text-center text-xs text-slate-500 hover:text-slate-300">
            Back to site
          </a>
        </form>
      </div>
    );
  }

  const filtered = (leads || []).filter((l) => filter === "all" || l.source === filter);
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const thisWeek = (leads || []).filter((l) => new Date(l.created_at).getTime() > weekAgo).length;

  const exportCsv = () => {
    const header = ["Name", "Company", "Email", "Phone", "Fleet Size", "CRM", "Trade", "Date"];
    const rows = filtered.map((l) => [
      l.name,
      l.company,
      l.email,
      l.phone,
      l.fleet_size || "",
      l.crm || "",
      l.source ? tradeLabel(l.source) : "",
      new Date(l.created_at).toISOString(),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "backfill-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0B0D10] px-5 py-10 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Logo size={32} />
            <div>
              <h1 className="font-display text-xl font-bold text-slate-50">Demo Requests</h1>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Backfill Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setPwOpen((o) => !o); setPwMsg(null); }}
              data-testid="admin-change-password-toggle"
              className="flex items-center gap-2 rounded-md border border-white/[0.12] px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-[#F25F22]/50 hover:text-slate-50"
            >
              <KeyRound className="h-4 w-4" /> Change Password
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-md border border-white/[0.12] px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-[#F25F22]/50 hover:text-slate-50"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        {pwOpen && (
          <form
            onSubmit={changePassword}
            data-testid="change-password-form"
            className="mt-6 grid gap-4 rounded-xl border border-white/[0.08] bg-[#12161F] p-6 sm:grid-cols-4"
          >
            <input
              type="password"
              required
              data-testid="pw-current-input"
              placeholder="Current password"
              value={pwForm.current}
              onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
              className={inputCls}
            />
            <input
              type="password"
              required
              minLength={10}
              data-testid="pw-new-input"
              placeholder="New password (10+ chars)"
              value={pwForm.next}
              onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
              className={inputCls}
            />
            <input
              type="password"
              required
              data-testid="pw-confirm-input"
              placeholder="Confirm new password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              className={inputCls}
            />
            <button
              type="submit"
              data-testid="pw-submit-button"
              disabled={pwLoading}
              className="flex items-center justify-center gap-2 rounded-md bg-[#F25F22] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-60"
            >
              {pwLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update"}
            </button>
            {pwMsg && (
              <p
                data-testid="pw-message"
                className={`sm:col-span-4 text-sm ${pwMsg.ok ? "text-emerald-400" : "text-red-400"}`}
              >
                {pwMsg.text}
              </p>
            )}
          </form>
        )}

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          {[
            { id: "leads", label: "Demo Requests", icon: LeadsIcon, testid: "tab-leads" },
            { id: "warm", label: "Warm Leads", icon: Flame, testid: "tab-warmleads" },
            { id: "engine", label: "Recovery Engine", icon: PhoneCall, testid: "tab-engine" },
            { id: "messages", label: "Messages", icon: MessageSquare, testid: "tab-messages" },
            { id: "estimates", label: "Estimates", icon: FileText, testid: "tab-estimates" },
            { id: "customers", label: "Customers", icon: Building2, testid: "tab-customers" },
            { id: "media", label: "Media", icon: ImageIcon, testid: "tab-media" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`relative flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? "bg-[#F25F22] text-white"
                  : "border border-white/[0.1] text-slate-400 hover:text-slate-100"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {t.id === "engine" && unread > 0 && (
                <span
                  data-testid="engine-unread-badge"
                  className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 font-mono text-[10px] font-bold text-white"
                >
                  {unread}
                </span>
              )}
              {t.id === "messages" && msgUnread > 0 && (
                <span
                  data-testid="messages-unread-badge"
                  className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 font-mono text-[10px] font-bold text-white"
                >
                  {msgUnread}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "engine" && <div className="mt-6"><ConversationsPanel token={token} onUnreadChange={setUnread} /></div>}
        {tab === "messages" && <div className="mt-6"><MessagesPanel token={token} onUnreadChange={setMsgUnread} /></div>}
        {tab === "warm" && <div className="mt-6"><WarmLeadsPanel token={token} /></div>}
        {tab === "estimates" && <div className="mt-6"><EstimatesPanel token={token} /></div>}
        {tab === "customers" && <div className="mt-6"><CustomersPanel token={token} /></div>}
        {tab === "media" && <div className="mt-6"><MediaPanel token={token} /></div>}

        {tab === "leads" && <>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <div className="rounded-lg border border-white/[0.08] bg-[#12161F] px-5 py-3" data-testid="admin-total-chip">
            <span className="font-display text-2xl font-bold text-slate-50">{leads?.length ?? "…"}</span>
            <span className="ml-2 text-xs text-slate-400">total requests</span>
          </div>
          <div className="rounded-lg border border-white/[0.08] bg-[#12161F] px-5 py-3" data-testid="admin-week-chip">
            <span className="font-display text-2xl font-bold text-[#F25F22]">{leads ? thisWeek : "…"}</span>
            <span className="ml-2 text-xs text-slate-400">this week</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="admin-trade-filter"
              className="rounded-md border border-white/[0.1] bg-[#12161F] px-4 py-2.5 text-sm text-slate-200 outline-none focus:border-[#F25F22]/60"
            >
              {TRADE_FILTERS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <button
              onClick={exportCsv}
              disabled={!filtered.length}
              data-testid="admin-export-csv"
              className="flex items-center gap-2 rounded-md border border-white/[0.12] bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:border-[#F25F22]/50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Download className="h-4 w-4 text-[#F25F22]" /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-white/[0.08] bg-[#12161F]">
          {leads === null ? (
            <div className="flex items-center justify-center gap-2 p-16 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-16 text-slate-500" data-testid="admin-empty-state">
              <Inbox className="h-8 w-8" />
              <p className="text-sm">No demo requests yet{filter !== "all" ? ` for ${tradeLabel(filter)}` : ""}.</p>
            </div>
          ) : (
            <table className="w-full min-w-[820px] text-left text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-white/[0.08] font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {["Name", "Company", "Contact", "Fleet", "CRM", "Trade", "Date", ""].map((h) => (
                    <th key={h} className="px-5 py-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-white/[0.05] last:border-0 hover:bg-white/[0.02]">
                    <td className="px-5 py-3.5 font-medium text-slate-100">{l.name}</td>
                    <td className="px-5 py-3.5 text-slate-300">
                      {l.company}
                      {l.preferred_date && (
                        <div className="mt-0.5 font-mono text-[11px] text-[#F25F22]">
                          Demo: {l.preferred_date} · {l.preferred_window}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-slate-400">
                      <div>{l.email}</div>
                      <div className="text-xs text-slate-500">{l.phone}</div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-400">{l.fleet_size || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-400">{l.crm || "—"}</td>
                    <td className="px-5 py-3.5">
                      <span className="rounded-full border border-[#F25F22]/30 bg-[#F25F22]/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#F25F22]">
                        {l.source ? tradeLabel(l.source) : "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-500">
                      {new Date(l.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => deleteLead(l.id)}
                        disabled={deleting === l.id}
                        data-testid={`delete-lead-${l.id}`}
                        title="Delete this request"
                        className="rounded-md p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-40"
                      >
                        {deleting === l.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>}
      </div>
    </div>
  );
}
